import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@ooh/ui/components/button';
import { Input } from '@ooh/ui/components/input';
import { InlineErrorMessage } from '@ooh/ui/components/inline-error-message';
import type { AvailabilityImportUploadResponse } from '@ooh/contracts';
import { UploadDropzoneSection, ScopeNote } from './upload-dropzone-section';
import { useUploadAvailabilityFile } from '../api/mutations';
import { isAcceptedAvailabilityFile } from '../lib/upload-file-validation';

interface UploadPhaseProps {
  /** Called with the full upload response and the originally-selected File
   * after a successful upload — the header-row wizard step needs the
   * file's name/size, which the upload response itself does not carry. */
  onNext: (response: AvailabilityImportUploadResponse, file: File) => void;
}

/**
 * Upload phase of the availability-import wizard: a single-file XLSX
 * dropzone that posts to `POST /availability-import/upload`. Mirrors
 * `apps/mockups/src/screens/components/availability-import/upload-phase.mockup.tsx`
 * (merged design source of truth, PR #708), wired to the real endpoint.
 */
export function UploadPhase({ onNext }: UploadPhaseProps) {
  const { t } = useTranslation('availabilityImport');
  const [selectedFile, setSelectedFile] = React.useState<File | null>(null);
  const [fileTypeError, setFileTypeError] = React.useState(false);
  const fileInputRef = React.useRef<HTMLInputElement>(null);

  const uploadMutation = useUploadAvailabilityFile(onNext);

  function handleFilesSelected(files: File[]) {
    const file = files[0];
    if (file === undefined) return;
    // Client-side gate: the file input's accept=".xlsx,.xls,.xlsm" is only a
    // picker hint — drag-and-drop and "All Files" selections bypass it, so an
    // unsupported format (e.g. a supplier's .ods file) must still be rejected
    // here instead of reaching the server. See CU-86cb1npz2.
    if (!isAcceptedAvailabilityFile(file)) {
      setFileTypeError(true);
      setSelectedFile(null);
      return;
    }
    setFileTypeError(false);
    setSelectedFile(file);
  }

  function handleUpload() {
    if (selectedFile === null) return;
    uploadMutation.mutate(selectedFile);
  }

  const isUploadDisabled = selectedFile === null || uploadMutation.isPending;

  return (
    <div className="max-w-2xl mx-auto space-y-6" data-testid="phase-upload">
      <UploadDropzoneSection
        title={t('upload.dropZoneTitle')}
        subtitle={t('upload.dropZoneSubtitle')}
        ariaLabel={t('upload.dropZoneTitle')}
        onClick={() => fileInputRef.current?.click()}
        onDragOver={(e) => e.preventDefault()}
        onDrop={(e) => {
          e.preventDefault();
          handleFilesSelected(Array.from(e.dataTransfer.files));
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') fileInputRef.current?.click();
        }}
        selectFileButton={
          <>
            <Button
              variant="outline"
              size="sm"
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                fileInputRef.current?.click();
              }}
            >
              {t('upload.selectFile')}
            </Button>
            <Input
              ref={fileInputRef}
              type="file"
              accept=".xlsx,.xls,.xlsm"
              className="hidden"
              data-testid="file-input"
              onChange={(e) => {
                handleFilesSelected(Array.from(e.target.files ?? []));
                // Reset so re-selecting a file with the same name still fires onChange.
                e.target.value = '';
              }}
            />
          </>
        }
      />

      {fileTypeError ? (
        <InlineErrorMessage testId="file-type-error">
          {t('upload.errorInvalidFileType')}
        </InlineErrorMessage>
      ) : null}

      {selectedFile !== null ? (
        <div
          className="rounded-md border border-border bg-muted/30 px-4 py-3 text-sm"
          data-testid="selected-file-name"
        >
          {selectedFile.name}
        </div>
      ) : null}

      <ScopeNote text={t('upload.scopeNote')} />

      <div className="flex justify-end pt-2">
        <Button
          type="button"
          onClick={handleUpload}
          disabled={isUploadDisabled}
          data-testid="next-to-preview"
        >
          {uploadMutation.isPending ? t('upload.uploading') : t('upload.nextButton')}
        </Button>
      </div>
    </div>
  );
}
